import { useEffect, useRef, useState } from 'react';

interface AddPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (promptText: string) => void | Promise<void>;
  loading?: boolean;
  title?: string;
  label?: string;
  placeholder?: string;
  confirmText?: string;
  initialValue?: string;
}

export function AddPromptModal({
  isOpen,
  onClose,
  onConfirm,
  loading = false,
  title = 'Add Prompt',
  label = 'Prompt text',
  placeholder = 'e.g. what are the best tools for small businesses?',
  confirmText = 'Add Prompt',
  initialValue = '',
}: AddPromptModalProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [promptText, setPromptText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setPromptText(initialValue);
      setIsVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
          setTimeout(() => {
            inputRef.current?.focus();
          }, 50);
        });
      });
    } else if (isVisible) {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isVisible, initialValue]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !loading) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isVisible, loading, onClose]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!promptText.trim()) return;
    await onConfirm(promptText.trim());
  };

  if (!isVisible) return null;

  return (
    <div
      className={`rename-modal ${isVisible ? 'is-visible' : ''} ${isAnimating ? 'is-open' : ''} ${
        !isAnimating && isVisible ? 'is-closing' : ''
      }`}
    >
      <div className="white-background">
        <div
          className="modal-close"
          onClick={loading ? undefined : onClose}
          style={{ cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1 }}
        >
          <div className="rotated-text">+</div>
        </div>

        <div className="text-size-medium text-weight-bold">{title}</div>
        <div className="spacer-xsmall"></div>

        <form onSubmit={handleSubmit} className="add-folder-form-block w-form">
          <label htmlFor="prompt-text-modal" className="form_label">
            {label}
          </label>
          <input
            ref={inputRef}
            id="prompt-text-modal"
            type="text"
            className="form_input w-input"
            value={promptText}
            onChange={(event) => setPromptText(event.target.value)}
            placeholder={placeholder}
            maxLength={256}
            required
            disabled={loading}
          />
          <div className="spacer-small"></div>
          <button
            type="submit"
            className={`button w-button${promptText.trim() ? '' : ' is-lighter'}`}
            disabled={loading || !promptText.trim()}
            style={{ opacity: loading ? 0.6 : 1, width: '100%' }}
          >
            {loading ? 'Please wait...' : confirmText}
          </button>
        </form>
      </div>

      <div
        className="close-layer"
        onClick={loading ? undefined : onClose}
        style={{ cursor: loading ? 'not-allowed' : 'pointer' }}
      ></div>
    </div>
  );
}
